import React, { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'ahooks';
import { SearchIcon } from 'lucide-react';
import { Grid } from '@giphy/react-components';
import { cn } from '@rocco/utils/cn';
import { Input } from '@rocco/components/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { useGiphy, type IGif } from '~/modules/giphy';
import poweredByGiphy from '~/modules/giphy/images/PoweredByGiphy_White_HorizText.png';

/**
 * A Giphy GIF picker popover. Shows trending GIFs by default and allows searching.
 */
export function GiphyPicker({
    children,
    width = 360,
    onSelect,
}: {
    children: React.ReactNode;
    width?: number;
    onSelect?: (gif: IGif, url: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search.trim(), { wait: 300 });
    const gf = useGiphy();

    const fetchGifs = useCallback(
        (offset: number) => {
            const options = {
                type: 'gifs',
                rating: 'pg',
                offset,
                limit: 20,
            } as const;
            if (debouncedSearch) {
                return gf.search(debouncedSearch, options);
            }
            return gf.trending(options);
        },
        [gf, debouncedSearch]
    );

    // Reset search state when popover opens.
    useEffect(() => {
        setSearch('');
    }, [open]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            {children && <PopoverTrigger asChild>{children}</PopoverTrigger>}
            <PopoverContent
                className="p-0"
                style={{ width }}
                side="top"
                align="center"
            >
                {/* Search input. */}
                <Input
                    className={cn(
                        'px-[12px] py-[8px] pl-10 text-[16px] md:text-[12px]',
                        !!search && 'pr-10'
                    )}
                    color="gray"
                    value={search}
                    placeholder="Search GIFs..."
                    showClearButton={!!search}
                    prefix={<SearchIcon className="size-4 text-gray-500" />}
                    autoFocus
                    onChange={(e) => setSearch(e.target.value)}
                />
                {/* GIF grid with infinite scroll. */}
                <div className="flex h-[300px] justify-center overflow-y-auto">
                    {open && (
                        <Grid
                            key={debouncedSearch}
                            className="min-h-full"
                            width={width - 20}
                            columns={2}
                            gutter={4}
                            borderRadius={4}
                            hideAttribution
                            noResultsMessage={
                                <div className="grid h-full place-items-center text-center text-[12px] text-gray-500">
                                    No GIFs found
                                </div>
                            }
                            fetchGifs={fetchGifs}
                            onGifClick={(gif, e) => {
                                e.preventDefault();
                                const mediaUrl = gif.images.original.url;
                                // Strip the query params.
                                const url = mediaUrl.split('?')[0]!;
                                setOpen(false);
                                onSelect?.(gif, url);
                            }}
                        />
                    )}
                </div>
                {/* Powered by Giphy attribution */}
                <div className="grid place-items-center py-[4px]">
                    <img
                        className="h-[12px] select-none"
                        src={poweredByGiphy}
                        alt="Powered by GIPHY"
                        loading="lazy"
                        draggable={false}
                    />
                </div>
            </PopoverContent>
        </Popover>
    );
}
